import { ApiRequest, PagedList } from 'framework/api';
import { Incident } from 'areas/incidents/incident';
import { RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { GridStore } from 'controls/grid/gridStore';
import { GridSelection } from 'controls/grid/gridSelection';
import { GridDataItem } from 'controls/grid/gridDataItem';

const endpoint = 'incidents/summaryList';

export type GetIncidentsArgs = {
	accountId?: string;
	includeSubaccounts?: boolean;
	entityType?: 'service' | 'asset' | 'default';
	entityId?: string;
	tagsPayload?: string[];
	showUntagged?: boolean;
};

export function getIncidents(args?: GetIncidentsArgs) {
	return new ApiRequest<PagedList<GridDataItem<Incident>>>({
		url: getIncidentsUrl(args),
		accountId: args?.accountId,
		accountBased: true,
		subaccountsFilter: true,
		includeSubaccounts: args?.includeSubaccounts,
		method: 'POST',
		payload: {
			tags: args?.tagsPayload,
			showUntagged: args?.showUntagged,
		},
	});
}

function getIncidentsUrl(args?: GetIncidentsArgs) {
	if (args?.entityType == 'service') {
		return `services/${args.entityId}/${endpoint}`;
	}

	if (args?.entityType == 'asset') {
		return `assets/${args.entityId}/${endpoint}`;
	}

	return endpoint;
}

export function getIncidentsRulesConfiguration() {
	return new ApiRequest<RulesConfiguration>({
		url: `${endpoint}/ruleConfiguration`,
		accountBased: true,
		subaccountsFilter: true,
		method: 'GET',
	});
}

export function setIncidentTags(store: GridStore<Incident>, selection: GridSelection<Incident>, tags: string[]) {
	return store.getSelectionApiRequest({
		url: `${endpoint}/setTags`,
		payload: {
			bulkTags: tags,
		},
		selection,
	});
}

export function deleteIncidents(store: GridStore<Incident>, selection: GridSelection<Incident>) {
	return store.getSelectionApiRequest<Incident[]>({
		url: `${endpoint}/delete`,
		selection,
	});
}

type ChangeIncidentsStatusArgs = {
	accountId: string;
	contentCategory: string;
	selection: GridSelection<Incident>;
	status: string;
	store: GridStore<Incident>;
};

export function changeIncidentsStatus(args: ChangeIncidentsStatusArgs) {
	return args.store.getSelectionApiRequest<Incident[]>({
		url: `${endpoint}/updateStatus`,
		selection: args.selection,
		payload: {
			bulkStatusId: args.status,
			bulkAccountId: args.accountId,
			bulkContentCategory: args.contentCategory,
		},
	});
}

export function exportToCsv(store: GridStore<Incident>, selection: GridSelection<Incident>) {
	const request = store.getSelectionApiRequest({
		url: `${endpoint}/exportCSV`,
		selection,
		preserveSort: true,
	});

	request.payload.fields = store.columns.visible.map((x) => x.field).filter((x) => x != 'id');

	return request;
}

export type IncidentStatus = {
	label: string;
	value: string;
};

type IncidentStatusResponse = {
	value: string;
	text: string;
};

type GetIncidentStatusesArgs = {
	accountId: string;
	contentCategory: string;
};

export function getIncidentStatuses({ accountId, contentCategory }: GetIncidentStatusesArgs) {
	return new ApiRequest<IncidentStatus[]>({
		url: `incidents/validStatusTypes`,
		accountBased: true,
		convertResultData: (statuses: IncidentStatusResponse[]) => {
			return statuses.map((x) => ({
				label: x.text,
				value: x.value,
			}));
		},
		payload: {
			accountId,
			contentCategory,
		},
	});
}
